"use client";

import { Button } from "@/components/shadcn/button";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { cn } from "@/lib/utils";
import { ArrowDownIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { createContext, useCallback, useContext, useLayoutEffect } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

const HoldScrollContext = createContext<() => void>(() => {});

export const useHoldScroll = () => useContext(HoldScrollContext);

const HoldScrollProvider = ({ children }: { children: ReactNode }) => {
  const { stopScroll } = useStickToBottomContext();
  const hold = useCallback(() => stopScroll(), [stopScroll]);
  return (
    <HoldScrollContext.Provider value={hold}>
      {children}
    </HoldScrollContext.Provider>
  );
};

export type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    className={cn("relative flex-1 overflow-y-hidden", className)}
    initial="instant"
    resize="smooth"
    role="log"
    {...props}
  />
);

export type ConversationContentProps = ComponentProps<"div"> & {
  compact?: boolean;
  children?: ReactNode;
};

export const ConversationContent = ({
  className,
  compact,
  children,
  ...props
}: ConversationContentProps) => {
  const { scrollRef, contentRef } = useStickToBottomContext();

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [scrollRef]);

  return (
    <ScrollArea
      className="h-full w-full"
      viewportRef={scrollRef}
      viewportClassName="[&>div]:flex! [&>div]:min-h-full [&>div]:flex-col"
    >
      <div
        className={cn(
          "flex grow flex-col p-4",
          compact ? "gap-4" : "gap-8",
          className
        )}
        ref={contentRef}
        {...props}
      >
        <HoldScrollProvider>{children}</HoldScrollProvider>
      </div>
    </ScrollArea>
  );
};

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          "absolute bottom-4 left-1/2 translate-x-[-50%] rounded-full",
          className
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};
